import {
  Box,
  Drawer,
  Menu,
  MenuItem,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  SwipeableDrawer,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import useResponsive from '../../hooks/useResponsive.js';
import AdminBottomNav from './AdminBottomNav';
import AdminHeader from './AdminHeader';
import AdminSidebar, {
  ADMIN_DRAWER_WIDTH,
  ADMIN_DRAWER_WIDTH_MINI,
} from './AdminSidebar';
import NavigationProgress from '../common/NavigationProgress';

import {
  AccountCircle as AccountCircleIcon,
  Add as AddIcon,
  QrCode2 as BatchIcon,
  Assessment as CountingIcon,
  Logout as LogoutIcon,
  PushPin as PinIcon,
  ReceiptLong as PurchasesIcon,
} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';

const QUICK_ACTIONS_RECENTS_KEY = 'admin.quickActions.recents.v1';
const QUICK_ACTIONS_PINS_KEY = 'admin.quickActions.pins.v1';
const QUICK_ACTIONS_HINT_DISMISSED_KEY = 'admin.quickActions.hintDismissed.v1';
const SIDEBAR_MODE_KEY = 'admin.sidebar.mode.v1';

// Get sidebar width based on mode
const getSidebarWidth = (mode) => {
  if (mode === 'full') return ADMIN_DRAWER_WIDTH;
  if (mode === 'mini') return ADMIN_DRAWER_WIDTH_MINI;
  return 48; // hidden mode - just the expand button
};

const AdminLayout = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const theme = useTheme();
  const { isMobile, isTouch } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [pinAnchorEl, setPinAnchorEl] = useState(null);
  const [pinCandidateKey, setPinCandidateKey] = useState(null);

  // Sidebar mode: 'full' | 'mini' | 'hidden'
  const [sidebarMode, setSidebarMode] = useState(() => {
    try {
      if (typeof localStorage === 'undefined') return 'full';
      const saved = localStorage.getItem(SIDEBAR_MODE_KEY);
      if (saved === 'full' || saved === 'mini' || saved === 'hidden')
        return saved;
      return 'full';
    } catch {
      return 'full';
    }
  });

  const handleSidebarModeChange = (newMode) => {
    setSidebarMode(newMode);
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(SIDEBAR_MODE_KEY, newMode);
      }
    } catch {
      // ignore storage errors
    }
  };

  const sidebarWidth = getSidebarWidth(sidebarMode);

  const [quickRecents, setQuickRecents] = useState(() => {
    try {
      if (typeof localStorage === 'undefined') return [];
      const raw = localStorage.getItem(QUICK_ACTIONS_RECENTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.filter((k) => typeof k === 'string')
        : [];
    } catch {
      return [];
    }
  });
  const [quickPins, setQuickPins] = useState(() => {
    try {
      if (typeof localStorage === 'undefined') return {};
      const raw = localStorage.getItem(QUICK_ACTIONS_PINS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const [showQuickHint, setShowQuickHint] = useState(() => {
    try {
      if (typeof localStorage === 'undefined') return false;
      return localStorage.getItem(QUICK_ACTIONS_HINT_DISMISSED_KEY) !== '1';
    } catch {
      return false;
    }
  });

  // If we navigated while the drawer is open, close it.
  useEffect(() => {
    if (drawerOpen) setDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const ALL_QUICK_ACTIONS = [
    {
      key: 'purchase-create',
      label: t('purchases.newPurchase'),
      icon: <PurchasesIcon />,
      to: '/purchases/create',
      contexts: ['purchases', 'inventory', 'home', 'default'],
    },
    {
      key: 'batch-create',
      label: t('nav.newBatch'),
      icon: <BatchIcon />,
      to: '/batches/create',
      contexts: ['inventory', 'home', 'default'],
    },
    {
      key: 'inventory-counting-create',
      label: t('nav.newCounting'),
      icon: <CountingIcon />,
      to: '/inventory-counting/create',
      contexts: ['inventory', 'home', 'default'],
    },
  ];

  const getQuickContext = (pathname) => {
    if (pathname.startsWith('/purchases')) return 'purchases';
    if (
      pathname.startsWith('/products') ||
      pathname.startsWith('/inventory') ||
      pathname.startsWith('/batches')
    )
      return 'inventory';
    if (pathname === '/' || pathname.startsWith('/dashboard')) return 'home';
    return 'default';
  };

  const quickContext = getQuickContext(location.pathname);
  const writeQuickRecents = (keys) => {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(QUICK_ACTIONS_RECENTS_KEY, JSON.stringify(keys));
    } catch {
      // ignore storage errors (private mode, blocked, etc.)
    }
  };

  const writeQuickPins = (nextPins) => {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(QUICK_ACTIONS_PINS_KEY, JSON.stringify(nextPins));
    } catch {
      // ignore storage errors
    }
  };

  const rememberQuickAction = (key) => {
    const next = [key, ...quickRecents.filter((k) => k !== key)].slice(0, 2);
    writeQuickRecents(next);
    setQuickRecents(next);
  };

  const togglePinnedQuickAction = (context, key) => {
    const current = quickPins?.[context];
    const nextPinned = current === key ? null : key;

    const nextPins = {
      ...(quickPins && typeof quickPins === 'object' ? quickPins : {}),
      [context]: nextPinned,
    };

    writeQuickPins(nextPins);
    setQuickPins(nextPins);
  };

  const openPinMenuForAction = (event, actionKey) => {
    event.preventDefault();
    event.stopPropagation();
    setPinCandidateKey(actionKey);
    setPinAnchorEl(event.currentTarget);
  };

  const closePinMenu = () => {
    setPinAnchorEl(null);
    setPinCandidateKey(null);
  };

  const dismissQuickHint = () => {
    setShowQuickHint(false);
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(QUICK_ACTIONS_HINT_DISMISSED_KEY, '1');
    } catch {
      // ignore
    }
  };

  const pinnedKey = quickPins?.[quickContext] ?? null;

  const isPinnedCandidate = pinCandidateKey && pinnedKey === pinCandidateKey;
  const isPinnedAction = (actionKey) => pinnedKey === actionKey;

  const quickActionsBase = ALL_QUICK_ACTIONS.filter((a) =>
    a.contexts.includes(quickContext)
  );
  const quickActions = [...quickActionsBase].sort((a, b) => {
    const pa = pinnedKey && a.key === pinnedKey ? 0 : 1;
    const pb = pinnedKey && b.key === pinnedKey ? 0 : 1;
    if (pa !== pb) return pa - pb;

    const ia = quickRecents.indexOf(a.key);
    const ib = quickRecents.indexOf(b.key);
    const ra = ia === -1 ? Number.POSITIVE_INFINITY : ia;
    const rb = ib === -1 ? Number.POSITIVE_INFINITY : ib;
    return ra - rb;
  });

  const pinnedAction = pinnedKey
    ? ALL_QUICK_ACTIONS.find(
        (a) => a.key === pinnedKey && a.contexts.includes(quickContext)
      )
    : null;

  return (
    <Box
      display="flex"
      minHeight="100vh"
      width="100%"
      maxWidth="100%"
      bgcolor="background.default"
      sx={{
        // Guard against any unexpected global centering / width clamps.
        // We keep this local to the admin shell to avoid affecting dialogs/portals.
        '&': {
          maxWidth: '100% !important',
          width: '100% !important',
        },
      }}
    >
      <NavigationProgress />
      {/* Fixed Sidebar for Desktop */}
      {!isMobile && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            width: sidebarWidth,
            zIndex: (theme) => theme.zIndex.drawer,
            transition: 'width 0.2s ease-in-out',
          }}
        >
          <AdminSidebar
            mode={sidebarMode}
            onModeChange={handleSidebarModeChange}
          />
        </Box>
      )}

      <Box
        flex={1}
        flexGrow={1}
        flexBasis={0}
        minWidth={0}
        display="flex"
        flexDirection="column"
        width="100%"
        maxWidth="100%"
        alignItems="stretch"
        sx={{
          // Offset for fixed sidebar on desktop
          ml: isMobile ? 0 : `${sidebarWidth}px`,
          transition: 'margin-left 0.2s ease-in-out',
        }}
      >
        <AdminHeader
          isMobile={isMobile}
          onMenuClick={() => setDrawerOpen(true)}
          onAvatarClick={handleProfileMenuOpen}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            flexGrow: 1,
            flexBasis: 0,
            alignSelf: 'stretch',
            width: '100% !important',
            minWidth: 0,
            maxWidth: 'none !important',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            boxSizing: 'border-box',
            p: isMobile ? 1.5 : 3,
            // Prevent first content block from visually touching/bleeding into sticky header
            // (especially noticeable on mobile browsers with dynamic address bar).
            pt: isMobile ? 'calc(56px + 12px)' : 3,
            pb: isMobile ? 'calc(72px + env(safe-area-inset-bottom))' : 3,
            // Hardening: never let inner wrappers (like MUI Container / PageContainer)
            // artificially clamp the content width inside the admin shell.
            '& .MuiContainer-root': {
              maxWidth: 'none !important',
              width: '100% !important',
              paddingLeft: '0 !important',
              paddingRight: '0 !important',
            },

            // Force all direct children to stretch full width
            '& > *': {
              width: '100% !important',
              maxWidth: 'none !important',
              minWidth: 0,
              flexShrink: 0,
              boxSizing: 'border-box',
            },

            // Guard: if some page applies negative margin-top, clamp it back.
            '& > *:first-of-type': {
              mt: 0,
            },
          }}
        >
          {children}
        </Box>

        {isMobile && <AdminBottomNav />}
      </Box>

      {isMobile && (
        <SpeedDial
          ariaLabel={t('dashboard.quickActions')}
          sx={{
            position: 'fixed',
            right: 16,
            // Keep above bottom nav + safe-area
            bottom: 'calc(72px + env(safe-area-inset-bottom) + 12px)',
            zIndex: (t) => t.zIndex.appBar + 1,
          }}
          icon={<SpeedDialIcon icon={<AddIcon />} />}
          open={quickOpen}
          onOpen={() => setQuickOpen(true)}
          onClose={() => setQuickOpen(false)}
        >
          {quickActions.map((action) => (
            <SpeedDialAction
              key={action.key}
              icon={
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {action.icon}
                  <Tooltip
                    title={
                      isPinnedAction(action.key) ? t('nav.unpin') : t('nav.pin')
                    }
                  >
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={
                        isPinnedAction(action.key)
                          ? t('nav.unpinAction').replace('{{label}}', action.label)
                          : t('nav.pinAction').replace('{{label}}', action.label)
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        togglePinnedQuickAction(quickContext, action.key);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        togglePinnedQuickAction(quickContext, action.key);
                      }}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: 26,
                        height: 26,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isPinnedAction(action.key)
                          ? 'primary.main'
                          : 'background.paper',
                        borderRadius: '999px',
                        border: '1px solid',
                        borderColor: isPinnedAction(action.key)
                          ? 'primary.main'
                          : 'divider',
                        boxShadow: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isPinnedAction(action.key)
                            ? 'primary.dark'
                            : 'action.hover',
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <PinIcon
                        sx={{
                          fontSize: 16,
                          color: isPinnedAction(action.key)
                            ? 'primary.contrastText'
                            : 'text.primary',
                        }}
                      />
                    </Box>
                  </Tooltip>

                  {isPinnedAction(action.key) && (
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        inset: -6,
                        borderRadius: '999px',
                        border: `2px solid ${theme.palette.primary.main}`,
                        opacity: 0.35,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </Box>
              }
              tooltipTitle={action.label}
              tooltipOpen
              onMouseDown={(e) => {
                if (e.button !== 0) return;
                const t = setTimeout(
                  () => openPinMenuForAction(e, action.key),
                  520
                );
                // store timer on element to clear later without extra state
                e.currentTarget.__pinTimer = t;
              }}
              onMouseUp={(e) => {
                const t = e.currentTarget.__pinTimer;
                if (t) clearTimeout(t);
                e.currentTarget.__pinTimer = null;
              }}
              onMouseLeave={(e) => {
                const t = e.currentTarget.__pinTimer;
                if (t) clearTimeout(t);
                e.currentTarget.__pinTimer = null;
              }}
              onTouchStart={(e) => {
                const t = setTimeout(
                  () => openPinMenuForAction(e, action.key),
                  520
                );
                e.currentTarget.__pinTimer = t;
              }}
              onTouchEnd={(e) => {
                const t = e.currentTarget.__pinTimer;
                if (t) clearTimeout(t);
                e.currentTarget.__pinTimer = null;
              }}
              onContextMenu={(e) => openPinMenuForAction(e, action.key)}
              onClick={() => {
                setQuickOpen(false);
                rememberQuickAction(action.key);
                navigate(action.to);
              }}
            />
          ))}
        </SpeedDial>
      )}

      <Snackbar
        open={Boolean(showQuickHint) && Boolean(isMobile) && Boolean(quickOpen)}
        onClose={dismissQuickHint}
        autoHideDuration={7000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: `calc(88px + env(safe-area-inset-bottom))` }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={dismissQuickHint}
          sx={{ borderRadius: 2, maxWidth: 560 }}
        >
          {t('nav.pinHint')}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={pinAnchorEl}
        open={Boolean(pinAnchorEl)}
        onClose={closePinMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuItem
          onClick={() => {
            if (!pinCandidateKey) return;
            togglePinnedQuickAction(quickContext, pinCandidateKey);
            closePinMenu();
          }}
        >
          <PinIcon sx={{ mr: 1 }} />
          {isPinnedCandidate ? t('nav.unpin') : t('nav.pin')}
        </MenuItem>
      </Menu>

      {isMobile &&
        (isTouch ? (
          <SwipeableDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onOpen={() => setDrawerOpen(true)}
            disableDiscovery
            PaperProps={{ sx: { width: ADMIN_DRAWER_WIDTH } }}
          >
            <AdminSidebar mobile onNavigate={() => setDrawerOpen(false)} />
          </SwipeableDrawer>
        ) : (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: ADMIN_DRAWER_WIDTH } }}
          >
            <AdminSidebar mobile onNavigate={() => setDrawerOpen(false)} />
          </Drawer>
        ))}

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate('/profile');
            handleProfileMenuClose();
          }}
        >
          <AccountCircleIcon sx={{ mr: 1 }} />
          {t('nav.myProfile')}
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          {t('auth.logout')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AdminLayout;
